import { useCallback, useEffect, useRef, useState } from 'react';
import type { GameActionRequest, ServerWsMessage, WebSearchSource } from '@orborix/shared-types';
import {
  encodeClientWsMessage,
  gameActionPatch,
  parseServerWsMessage,
} from '@orborix/shared-types';
import { MicCapture } from './MicCapture.js';
import { requestAudioFocus, type AudioFocusHandle } from './audioFocus.js';
import {
  setOrbAudioPlayingGetter,
  setOrbEnergyGetter,
  setOrbResponseStartedGetter,
} from './orbEnergyChannel.js';
import {
  LOST_AUTO_RETRY_MS,
  MAX_LOST_AUTO_RETRIES,
  MAX_RECONNECT_ATTEMPTS,
  MIC_SETTLE_MS,
  reconnectDelayMs,
  STABLE_CONNECTION_MS,
  WS_CLOSE_INSUFFICIENT_CREDITS,
} from './useVoiceSession/constants.js';
import { createSettleDropCounter } from './useVoiceSession/micSettleCounter.js';
import {
  buildSessionUrl,
  createMicPcmHandler,
  createPlayerAndMic,
  onWsOpen,
  teardownConnection,
} from './useVoiceSession/connection.js';
import {
  clearKaraokeBuffer,
  createKaraokeBufferState,
  getKaraokeCharIndex,
  getKaraokeRenderEndIdx,
} from './useVoiceSession/karaokeBuffer.js';
import {
  clearReattachSession,
  persistReattachSession,
  readFreshReattachSession,
} from './useVoiceSession/reattachStore.js';
import { createServerMessageHandler } from './useVoiceSession/serverMessageHandler.js';
import { buildSessionActions, type PendingCommit } from './useVoiceSession/sessionActions.js';
import type {
  AudioPlayerLike,
  ClientIOMode,
  CommitTurnOptions,
  ConnectionStatus,
  GameRoundLive,
  GameSolutionLive,
  MicCaptureLike,
  UseVoiceSessionOptions,
  UseVoiceSessionReturn,
  VoiceMessage,
  VoiceState,
} from './useVoiceSession/types.js';

export type {
  AudioPlayerLike,
  ClientIOMode,
  MicCaptureLike,
  PronunciationCorrection,
  PronunciationQualificationsLite,
  PronunciationReportLite,
  UseVoiceSessionOptions,
  UseVoiceSessionReturn,
  VoiceMessage,
  VoiceMessageCorrection,
  VoiceState,
} from './useVoiceSession/types.js';

/**
 * React hook driving a single voice session over WS:
 *  - opens the WS, sends `{type:'start'}`
 *  - streams mic PCM (binary) → backend
 *  - decodes server JSON for state/transcript and binary as TTS audio
 *  - {@link UseVoiceSessionReturn.interrupt} resolves when server acks
 *
 * El hook es un orquestador. La lógica vive en sub-módulos bajo
 * `./useVoiceSession/`:
 *   - connection.ts        — URL building, factories, teardown, mic gate
 *   - serverMessageHandler — switch del WS message handler
 *   - sessionActions       — acciones públicas que tocan el WS
 *   - karaokeBuffer        — buffer de alignment + getters de revelado
 *   - messageAnchors       — helpers puros de anclaje al `messages[]`
 */
export function useVoiceSession(opts: UseVoiceSessionOptions = {}): UseVoiceSessionReturn {
  const [state, setStateInternal] = useState<VoiceState>('idle');
  const [transcript, setTranscript] = useState('');
  const [assistantText, setAssistantText] = useState('');
  const [messages, setMessages] = useState<ReadonlyArray<VoiceMessage>>([]);
  // Fuentes de la búsqueda web del turno EN CURSO, expuestas apenas el texto
  // del coach queda completo (state→listening), sin esperar el fin del audio
  // (que dispara el commit). Se limpian al commitear (las fuentes pasan a
  // `VoiceMessage.sources` de la burbuja). Deja mostrar el chip bajo la burbuja
  // viva ni bien se termina de escribir, no cuando termina de hablar.
  const [liveWebSources, setLiveWebSources] = useState<ReadonlyArray<WebSearchSource>>([]);
  const [sessionId, setSessionId] = useState<string | null>(null);
  // The live translate-it round (`game.round`, plan 47); null until the first
  // round message of the connection.
  const [gameRound, setGameRound] = useState<GameRoundLive | null>(null);
  // The solutions of this connection (`game.solution`, plan 47 Round 2).
  const [gameSolutions, setGameSolutions] = useState<ReadonlyArray<GameSolutionLive>>([]);
  const addGameSolution = useCallback((solution: GameSolutionLive): void => {
    setGameSolutions((prev) => [...prev, solution]);
  }, []);
  const [muted, setMuted] = useState(false);
  const [paused, setPaused] = useState(false);
  const [ioMode, setIoModeState] = useState<ClientIOMode>(
    opts.initialIoMode ?? { input: 'voice', output: 'voice' },
  );
  const [translationEnabled, setTranslationEnabledState] = useState<boolean>(
    opts.initialTranslation?.enabled ?? false,
  );
  const [translationTargetLang, setTranslationTargetLangState] = useState<string>(
    (opts.initialTranslation?.targetLang ?? 'es').toLowerCase(),
  );
  const [speechRate, setSpeechRateState] = useState<number>(opts.initialSpeechRate ?? 1);
  const [lastTtsFirstByteMs, setLastTtsFirstByteMs] = useState<number | null>(null);
  const [isAudioPlaying, setIsAudioPlaying] = useState(false);
  const audioFocusRef = useRef<AudioFocusHandle | null>(null);
  /**
   * `true` desde que otro audio nos desplazó hasta que el server confirma el
   * interrupt. Sin esto, los chunks de TTS que ya venían en vuelo cuando
   * cortamos volverían a pedir el foco y le arrebatarían el audio al replay que
   * el usuario acaba de apretar (ping-pong).
   */
  const audioFocusYieldedRef = useRef(false);
  /**
   * Único punto donde el TTS en vivo entra y sale del foco de audio global (ver
   * `audioFocus.ts`): mientras el coach suena, es el dueño; cuando termina, se
   * corta o la sesión se cierra, lo suelta.
   */
  const setIsAudioPlayingFocused = useCallback((next: boolean): void => {
    setIsAudioPlaying(next);
    if (!next) {
      audioFocusRef.current?.release();
      audioFocusRef.current = null;
      return;
    }
    if (audioFocusYieldedRef.current || audioFocusRef.current?.active) return;
    audioFocusRef.current = requestAudioFocus('voice-session', () => {
      // Con la sesión pausada el AudioContext ya está congelado: no hay nada
      // sonando que callar, así que soltamos el foco y listo. Cortar el turno
      // acá sería destruir una respuesta que el usuario dejó a medias a
      // propósito para escuchar otra cosa.
      if (pausedRef.current) return;
      // Otro audio pidió sonar (el replay de una burbuja, Escuchar, una
      // canción). Al coach en vivo no alcanza con bajarle el volumen local:
      // hay que cortarlo en el server, que es exactamente lo mismo que pasa
      // cuando el usuario le habla encima. El texto que alcanzó a decir se
      // commitea al historial (lo hace `interrupt`).
      audioFocusYieldedRef.current = true;
      void actionsRef.current.interrupt().finally(() => {
        audioFocusYieldedRef.current = false;
      });
    });
  }, []);
  // `true` cuando el server graba el audio crudo de la sesión (RECORD_SESSION_AUDIO).
  // Lo emite `session.started`; gatea el play del audio real del usuario.
  const [audioRecording, setAudioRecording] = useState(false);
  // Modo de conversación anunciado por el server en `session.started` (plan
  // realtime, decisión #20). El ref gatea el bypass half-duplex del mic (leído
  // por-chunk en `createMicPcmHandler`); el estado expone el modo a la app.
  const [conversationMode, setConversationMode] = useState<'realtime' | 'classic'>('classic');
  const realtimeModeRef = useRef(false);
  // Modo realtime — id del ciclo de respuesta de OpenAI cuya burbuja del bot
  // está viva (F4b, decisión #16): los `tokens` viajan keyeados por
  // `responseId` y un id nuevo commitea la burbuja anterior. En clásico queda
  // null siempre (el karaoke maneja el revelado).
  const realtimeLiveResponseIdRef = useRef<string | null>(null);
  // ¿La sesión produce correcciones? Lo anuncia `session.started { corrections }`
  // (gate del side-runner: off en examen de nivel / A0 sin misión / módulo sin
  // runner). Gatea el skeleton "analizando…" de los user msgs — sin side-runner
  // nunca llega el `corrections.done` que lo limpia (card #318).
  const correctionsEnabledRef = useRef(true);
  // F4c: la pausa server-side la puso el ciclo background/foreground (no el
  // user) — sólo entonces el foreground auto-resume. Nunca pisa una pausa
  // manual.
  const backgroundAutoPausedRef = useRef(false);
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>('idle');
  // Push-to-talk: commit en vuelo (tap de enviar → transcript.final). La UI
  // lo usa como feedback "enviando…" — sin esto el tap se siente frizado
  // mientras el STT commitea (300-1000ms medidos, 2026-07-26). El contador de
  // turnos vacíos avisa cuando un envío murió sin transcript (audio perdido /
  // commit ignorado) para que la app muestre "no se escuchó".
  const [commitPending, setCommitPending] = useState(false);
  const commitPendingRef = useRef(false);
  const [turnEmptyCount, setTurnEmptyCount] = useState(0);
  // Commit del push-to-talk todavía sin `turn-stop` (post-roll / flush del
  // worklet). Ver `commitTurn` en sessionActions.
  const pendingCommitRef = useRef<PendingCommit | null>(null);
  const markCommitPending = useCallback((): void => {
    commitPendingRef.current = true;
    setCommitPending(true);
  }, []);
  const clearCommitPending = useCallback((): void => {
    if (!commitPendingRef.current) return;
    commitPendingRef.current = false;
    setCommitPending(false);
  }, []);
  const onTurnEmpty = useCallback((): void => {
    commitPendingRef.current = false;
    setCommitPending(false);
    setTurnEmptyCount((c) => c + 1);
  }, []);
  // El reply del coach falló (`reply_failed`). Vive hasta que la charla sigue
  // por otro lado: Reintentar, un turno nuevo del user, texto del coach, o una
  // conexión nueva (el pipeline nuevo ya no tiene el fallo para reintentar).
  const [replyFailed, setReplyFailed] = useState(false);
  const onReplyFailed = useCallback((): void => setReplyFailed(true), []);
  const clearReplyFailed = useCallback((): void => setReplyFailed(false), []);

  // ── Refs ─────────────────────────────────────────────────────────────
  // Estado mirror: leído sincrónicamente desde handlers del WS y RAF sin
  // disparar re-suscripciones.
  const micSettleUntilRef = useRef(0);
  // Coda hablada de correcciones (manos libres): su audio llega por el stream
  // binario SIN state/turn-end (ver serverMessageHandler.handleBinary), así que
  // no re-arma `micSettleUntilRef` por las vías normales. Este flag dedupea el
  // `awaitIdle` que sí lo re-arma tras el fin acústico de la coda.
  const codaSettlePendingRef = useRef(false);
  // Diagnóstico de cuánto muerde la ventana de settle en uso real (cada cuánto
  // se tira audio de alguien que contestó rápido). No cambia ninguna decisión
  // del camino del audio. Ver `micSettleCounter.ts`.
  const settleCounterRef = useRef(createSettleDropCounter());
  const mutedRef = useRef(false);
  const pausedRef = useRef(false);
  const ioModeRef = useRef<ClientIOMode>(ioMode);
  const prevStateRef = useRef<VoiceState>('idle');
  const stateRef = useRef<VoiceState>('idle');
  const assistantTextRef = useRef('');
  const ttsStopwatchRef = useRef<number | null>(null);
  const captureExternallyPausedRef = useRef(false);
  const lastFinalTranscriptRef = useRef<string | null>(null);
  // Buffer de traducción del turno del bot (Fase 28) — la traducción llega
  // ~300ms post-LLMFullResponseEnd pero el bot bubble se commitea recién en
  // state speaking→listening. Stashed acá, aplicada en el commit.
  const pendingBotTranslationRef = useRef<{ text: string; targetLang: string } | null>(null);
  // Fuentes de la búsqueda web del turno en curso, a la espera de anclarse a la
  // burbuja del bot cuando se commitea (mismo patrón que la traducción pendiente).
  const pendingBotWebSourcesRef = useRef<ReadonlyArray<WebSearchSource> | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const micRef = useRef<MicCaptureLike | null>(null);
  // Cuántas veces ESTA sesión soltó el mic a propósito (background, pantalla
  // bloqueada, cierre). El `start()` del open del WS captura el valor antes de
  // arrancar: si cuando su bootstrap muere con `mic_disposed` el contador
  // cambió, la cancelación fue NUESTRA y el reintento revivería un mic que
  // pedimos soltar. Es un contador y no un flag porque la señal es "hubo un
  // teardown mientras yo arrancaba", no el estado actual. El caso que sólo él
  // ve es el bloqueo de pantalla: llega por el `release` del wake lock, sin
  // `visibilitychange`, así que la visibilidad sola no lo distingue. Para el
  // cierre de sesión es redundante a propósito (el `cleanup` ya nuleó `wsRef`,
  // que es lo que corta el reintento).
  const micTeardownSeqRef = useRef(0);
  const playerRef = useRef<AudioPlayerLike | null>(null);
  const pendingAcks = useRef(new Map<string, () => void>());
  const interruptSeq = useRef(0);
  // ── Reconexión ─────────────────────────────────────────────────────────
  // `true` cuando el cierre del WS es deliberado: `stop()`/unmount del lado
  // app, o un cierre server-side (`session.ended` / `session.blocked` / close
  // 4402, vía `markServerSessionClosed`). El close handler NO debe reconectar
  // en ese caso. Se resetea en `start()` y en `reconnect()`.
  const intentionalCloseRef = useRef(false);
  // Intentos consumidos en la ráfaga de reconexión actual. Se resetea a 0
  // cuando la conexión se prueba ESTABLE (ver `stableTimerRef`) y al reconectar
  // manualmente.
  const reconnectAttemptsRef = useRef(0);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Timer que, al sobrevivir `STABLE_CONNECTION_MS` abierto, marca la conexión
  // como estable y resetea el presupuesto de reintentos. Se cancela en cada
  // close/cleanup: un socket que muere antes de la ventana NO resetea el
  // contador → una conexión que flapea agota los intentos y cae a `lost` en vez
  // de loopear para siempre. Ver JSDoc de `STABLE_CONNECTION_MS`.
  const stableTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Auto-reintentos consumidos YA en `lost` (los del efecto de abajo, no los de
  // la ráfaga con backoff). Se repone al estabilizar la conexión, en `start()` y
  // ante una señal nueva del browser (`online` / tab visible).
  const lostAutoRetriesRef = useRef(0);
  // Mirror sincrónico del `sessionId` para leerlo desde handlers del WS (al
  // reconectar lo pasamos como `resumeSessionId`).
  const sessionIdRef = useRef<string | null>(null);
  // Handler de mic compartido — se crea UNA vez y se re-suscribe sólo si los
  // devices se recrean. Lee `wsRef.current` en vivo, así que sobrevive a un
  // swap de socket sin re-wire.
  const micPcmHandlerRef = useRef<((chunk: ArrayBuffer) => void) | null>(null);
  // Refs a las funciones recursivas del loop de reconexión (rompen el ciclo
  // openConnection → scheduleReconnect → attemptReconnect → openConnection).
  const scheduleReconnectRef = useRef<() => void>(() => {});
  const attemptReconnectRef = useRef<() => void>(() => {});
  // Karaoke buffer (Fase 36+40) — mutable, leído desde RAF sin re-renderear.
  const karaokeRef = useRef(createKaraokeBufferState());
  // ¿Llegó el `turn-end` del turno del coach en curso? (= el server ya mandó
  // todo su audio). Con esto el corte sabe si lo recibido es el turno entero.
  const turnAudioCompleteRef = useRef(false);

  // Arma la ventana de settle en sus DOS puntas: el gate del pcm handler (que
  // descarta los bloques) y el pre-roll del noise gate del worklet (que si no
  // traería eco del coach al abrir apenas vence la ventana). En realtime el
  // settle no gatea nada (`createMicPcmHandler` lo bypasea) y el pre-roll
  // tampoco tiene por qué perderse.
  const armMicSettle = useCallback((): void => {
    micSettleUntilRef.current = Date.now() + MIC_SETTLE_MS;
    if (!realtimeModeRef.current) micRef.current?.armSettle?.(MIC_SETTLE_MS);
  }, []);

  // Wrapper: cualquier cambio de state se refleja en stateRef y arma el
  // settle window en la transición bot→listening.
  const setState = useCallback(
    (next: VoiceState): void => {
      const prev = stateRef.current;
      if ((prev === 'speaking' || prev === 'thinking') && next === 'listening') {
        armMicSettle();
      }
      stateRef.current = next;
      setStateInternal(next);
      // Refresca el `ts` del sessionId persistido mientras la charla está viva
      // (para sobrevivir un F5): cada transición de turno lo mantiene fresco, así
      // un F5 tras una charla larga sigue dentro del TTL. Skipeamos durante un
      // cierre deliberado (ahí `stop()` lo limpia).
      if (sessionIdRef.current && !intentionalCloseRef.current) {
        persistReattachSession(opts.moduleSlug, sessionIdRef.current);
      }
    },
    [opts.moduleSlug, armMicSettle],
  );

  // Setter de sessionId que mantiene `sessionIdRef` sincronizado para que los
  // handlers del WS (reconexión) lo lean sin pasar por el ciclo de render.
  const setSessionIdTracked = useCallback(
    (id: string | null): void => {
      sessionIdRef.current = id;
      setSessionId(id);
      // Persistir el sessionId (F5 survival): al recibir `session.started` o al
      // reattachear. `null` (reset de start()/stop()) no persiste; el clear
      // explícito lo hace `stop()`.
      if (id) persistReattachSession(opts.moduleSlug, id);
    },
    [opts.moduleSlug],
  );

  // Cancela cualquier reintento de reconexión programado.
  const clearReconnectTimer = useCallback((): void => {
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
  }, []);

  // Cancela el timer de "conexión estable" pendiente.
  const clearStableTimer = useCallback((): void => {
    if (stableTimerRef.current) {
      clearTimeout(stableTimerRef.current);
      stableTimerRef.current = null;
    }
  }, []);

  const cleanup = useCallback(
    async (o: { silent?: boolean } = {}) => {
      clearReconnectTimer();
      clearStableTimer();
      micTeardownSeqRef.current += 1;
      const ws = wsRef.current;
      const mic = micRef.current;
      const player = playerRef.current;
      wsRef.current = null;
      micRef.current = null;
      playerRef.current = null;
      pendingAcks.current.clear();
      await teardownConnection({ ws, mic, player }, o);
    },
    [clearReconnectTimer, clearStableTimer],
  );

  // Suelta el mic cuando la PWA va a background o la pantalla se bloquea:
  // detiene el track (apaga el indicador del browser y el "tiri-tiri" de
  // acople GSM que iOS mete cuando el mic queda tomado con la pantalla
  // apagada) Y resetea la instancia con `stop()` para que un re-arranque
  // posterior pueda recrear el worklet — sin el `stop()`, `start()` haría
  // early-return (`this.node` sigue seteado sobre un AudioContext cerrado) y
  // el mic quedaría muerto al volver. Idempotente.
  const teardownMicForBackground = useCallback((): void => {
    micTeardownSeqRef.current += 1;
    void micRef.current?.stop();
    void MicCapture.dispose().catch(() => {
      /* ignore */
    });
    // F4c (plan realtime §6c): en realtime, soltar el mic no alcanza — OpenAI
    // seguiría generando contra una cola local suspendida que descarta
    // (MAX_SUSPENDED_QUEUE_SEC). Pausamos la sesión server-side (el backend
    // cancela la respuesta en curso + descarta el input bufferizado en
    // OpenAI). Sólo si el user no la pausó él mismo; el flag distingue esta
    // pausa automática para que el foreground la levante solo. En classic no
    // se toca nada (el server no genera mientras no llega audio).
    if (realtimeModeRef.current && !pausedRef.current) {
      actionsRef.current.pauseSession();
      backgroundAutoPausedRef.current = pausedRef.current;
    }
  }, []);

  // Re-arma el mic al volver a foreground durante una sesión viva en modo voz.
  // `getUserMedia` puede re-promptear si iOS olvidó el permiso (mismo
  // trade-off de siempre); el guard `bootstrapping` de `ensureSharedMic`
  // evita doble prompt si la reconexión también dispara `start()`. Best-effort:
  // si falla, la próxima interacción / reconexión recupera.
  const rearmMicForForeground = useCallback((): void => {
    // Simétrico del auto-pause de background (F4c): sólo se resume la pausa
    // que puso ese mecanismo — una pausa manual del user queda como está.
    if (backgroundAutoPausedRef.current) {
      backgroundAutoPausedRef.current = false;
      actionsRef.current.resumeSession();
    }
    if (ioModeRef.current.input !== 'voice') return;
    void micRef.current?.start().catch(() => {
      /* ignore */
    });
  }, []);

  // Ciclo de vida del mic atado a la visibilidad. El teardown se dispara por
  // MÚLTIPLES señales porque en iOS PWA `visibilitychange` NO llega de forma
  // confiable al bloquear la pantalla — el `release` del Screen Wake Lock (más
  // abajo) cubre ese caso. Que dos vías llamen al teardown a la vez no rompe
  // nada (idempotente).
  useEffect(() => {
    const onVisibility = (): void => {
      if (document.visibilityState === 'hidden') {
        teardownMicForBackground();
      } else if (connectionStatus !== 'idle') {
        rearmMicForForeground();
      }
    };
    const onPagehide = (): void => teardownMicForBackground();
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', onPagehide);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', onPagehide);
    };
  }, [connectionStatus, teardownMicForBackground, rearmMicForForeground]);

  // Mantener la pantalla despierta mientras la conversación está viva — sin esto
  // el celu se bloquea solo a los segundos/minutos en modo manos libres (el
  // usuario no toca la pantalla mientras habla). El Screen Wake Lock se suelta
  // automáticamente cuando el tab pasa a background, así que al volver al
  // foreground hay que RE-pedirlo (de ahí el listener de visibilitychange).
  // Best-effort: si el browser no soporta la API o la rechaza (p. ej. batería
  // baja), no rompemos la sesión — es una mejora progresiva, no config.
  useEffect(() => {
    if (connectionStatus === 'idle') return;
    if (!('wakeLock' in navigator)) return;

    let sentinel: WakeLockSentinel | null = null;
    let disposed = false;

    const acquire = async (): Promise<void> => {
      if (disposed || sentinel || document.visibilityState !== 'visible') return;
      try {
        const granted = await navigator.wakeLock.request('screen');
        // El request es async: en esa ventana pudo correr el cleanup del efecto
        // (`disposed`) o pudo ganar otro `acquire()` (dos visibilitychange
        // seguidos). Un sentinel que llega tarde no tiene quién lo suelte —
        // pantalla despierta para siempre — así que lo liberamos acá, ANTES de
        // suscribir el `release` (ese listener es del sentinel vigente: dispararlo
        // con este soltaría el mic de una sesión que no lo pidió).
        if (disposed || sentinel) {
          void granted.release().catch(() => {
            /* ignore */
          });
          return;
        }
        sentinel = granted;
        granted.addEventListener('release', () => {
          sentinel = null;
          // Sólo el release DEL SISTEMA (pantalla apagada/bloqueada — señal
          // confiable en iOS, donde `visibilitychange` no llega al bloquear)
          // suelta el mic. El release que provoca nuestro PROPIO cleanup
          // (cambio de `connectionStatus`, ej. connecting→open, o unmount) no
          // significa que la pantalla se apagó: dispararlo acá caía en plena
          // ventana del `mic.start()` del open del WS y el dispose cancelaba
          // el bootstrap (`mic_disposed`) — sesión viva pero muda. El unmount
          // real ya libera el mic por el cleanup de la conexión.
          if (disposed) return;
          teardownMicForBackground();
        });
      } catch {
        /* el browser puede rechazar el lock — best-effort, no es crítico */
      }
    };

    const onVisible = (): void => {
      if (document.visibilityState === 'visible') void acquire();
    };

    void acquire();
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      disposed = true;
      document.removeEventListener('visibilitychange', onVisible);
      if (sentinel) {
        void sentinel.release().catch(() => {
          /* ignore */
        });
        sentinel = null;
      }
    };
  }, [connectionStatus, teardownMicForBackground]);

  // Handler de mic creado UNA vez (lee `wsRef.current` en vivo → sobrevive a un
  // swap de socket en la reconexión sin re-suscribir).
  const ensureMicPcmHandler = useCallback((): ((chunk: ArrayBuffer) => void) => {
    if (!micPcmHandlerRef.current) {
      micPcmHandlerRef.current = createMicPcmHandler({
        mutedRef,
        pausedRef,
        captureExternallyPausedRef,
        stateRef,
        micSettleUntilRef,
        playerRef,
        wsRef,
        realtimeModeRef,
        settleCounterRef,
      });
    }
    return micPcmHandlerRef.current;
  }, []);

  // Crea player + mic si no existen (los reusa entre reconexiones). El mic es
  // un singleton idempotente, así que llamarlo de nuevo no re-promptea permiso.
  const ensureDevices = useCallback(async (): Promise<void> => {
    if (micRef.current && playerRef.current) return;
    const { player, mic } = await createPlayerAndMic(opts);
    playerRef.current = player;
    micRef.current = mic;
    mic.on('pcm', ensureMicPcmHandler());
  }, [opts, ensureMicPcmHandler]);

  // El server cerró la sesión por iniciativa propia — cierre normal
  // (`session.ended`: Scout end_interview / idle-no-speech) o bloqueo terminal
  // (`session.blocked` + close 4402/4403: saldo agotado, safety L4 de coach).
  // El close que sigue es INTENCIONAL: sin esto, el cierre server-side dispara
  // la auto-reconexión sobre una fila finalizada (loop de sesiones fantasma) o
  // contra un backend que ya bloqueó (el preflight rebota cada intento hasta
  // `lost`, tapando el copy del bloqueo). También limpiamos el id persistido de
  // F5-survival: la fila quedó cerrada y NO es reattach-elegible; sin el clear,
  // un re-entry dentro del TTL mandaría `reconnectSessionId` de la sesión
  // muerta y el backend degradaría a resume (fila hija sin opener) en vez de
  // abrir limpio.
  const markServerSessionClosed = useCallback((): void => {
    intentionalCloseRef.current = true;
    clearReattachSession(opts.moduleSlug);
  }, [opts.moduleSlug]);

  // Abre un WS y cablea sus handlers. Asume que los devices ya existen. Si
  // `reconnectSessionId` está (auto-reconexión), el backend reusa esa fila
  // (reattach) o cae al resume. En el arranque inicial va sin él: "Continuar
  // conversación" viaja por separado como `moduleHints.resumeSessionId`.
  const openConnection = useCallback(
    (reconnectSessionId?: string): void => {
      const url = buildSessionUrl({ opts, ...(reconnectSessionId ? { reconnectSessionId } : {}) });
      console.info('[voice] opening WS', {
        moduleSlug: opts.moduleSlug ?? '(none → general_assistant)',
        captureMode: opts.captureMode ?? '(default free)',
        reconnectSessionId: reconnectSessionId ?? '(none)',
        url,
      });
      setReplyFailed(false);
      // A new connection starts without a known round: the server sends the
      // next one when a coach reply serves a challenge.
      setGameRound(null);
      setGameSolutions([]);
      const Ctor = opts.webSocketCtor ?? WebSocket;
      const ws = new Ctor(url);
      ws.binaryType = 'arraybuffer';
      wsRef.current = ws;

      const serverHandler = createServerMessageHandler({
        prevStateRef,
        stateRef,
        assistantTextRef,
        pendingBotTranslationRef,
        pendingBotWebSourcesRef,
        setLiveWebSources,
        lastFinalTranscriptRef,
        ttsStopwatchRef,
        armMicSettle,
        codaSettlePendingRef,
        settleCounterRef,
        turnAudioCompleteRef,
        pausedRef,
        ioModeRef,
        pendingAcks,
        wsRef,
        playerRef,
        karaoke: karaokeRef.current,
        setState,
        setTranscript,
        setAssistantText,
        setMessages,
        setSessionId: setSessionIdTracked,
        sessionIdRef,
        setGameRound,
        addGameSolution,
        setLastTtsFirstByteMs,
        setIsAudioPlaying: setIsAudioPlayingFocused,
        setPaused,
        setIoModeState,
        setTranslationEnabledState,
        setTranslationTargetLangState,
        setAudioRecording,
        commitPendingRef,
        clearCommitPending,
        onTurnEmpty,
        onReplyFailed,
        onReplyText: clearReplyFailed,
        realtimeModeRef,
        realtimeLiveResponseIdRef,
        setConversationMode,
        correctionsEnabledRef,
        onServerSessionClosed: markServerSessionClosed,
      });

      ws.addEventListener('open', () => {
        setConnectionStatus('open');
        // NO reseteamos el presupuesto de reintentos acá: un socket que abre y
        // se cae a los ms no es una conexión "sana". Sólo lo reseteamos si
        // sobrevive `STABLE_CONNECTION_MS` (abajo). Así una conexión que flapea
        // agota los intentos → `lost`, en vez de loopear infinito.
        clearStableTimer();
        stableTimerRef.current = setTimeout(() => {
          stableTimerRef.current = null;
          if (wsRef.current === ws) {
            reconnectAttemptsRef.current = 0;
            lostAutoRetriesRef.current = 0;
          }
        }, STABLE_CONNECTION_MS);
        const mic = micRef.current;
        if (mic) {
          const teardownSeqAtOpen = micTeardownSeqRef.current;
          onWsOpen({
            ws,
            mic,
            startMode: ioModeRef.current,
            setState,
            // Sólo se reintenta el bootstrap que canceló un dispose AJENO —
            // o sea: este socket sigue siendo el vivo de la sesión; el input
            // sigue en voz (pasar a texto es pedir el mic apagado, y
            // `onWsOpen` ni siquiera lo arranca en ese modo); nadie de acá
            // pidió soltarlo mientras arrancaba (contador de teardowns, la
            // única señal del bloqueo de pantalla, que llega por el `release`
            // del wake lock y no por `visibilitychange`); y el documento está
            // visible.
            canRetryMicStart: () =>
              wsRef.current === ws &&
              ioModeRef.current.input === 'voice' &&
              micTeardownSeqRef.current === teardownSeqAtOpen &&
              document.visibilityState === 'visible',
          });
        } else setState('listening');
      });

      ws.addEventListener('message', (event: MessageEvent) => {
        const data = event.data as unknown;
        if (data instanceof ArrayBuffer) {
          serverHandler.handleBinary(data);
          return;
        }
        if (typeof data !== 'string') return;
        let parsed: ServerWsMessage;
        try {
          parsed = parseServerWsMessage(data);
        } catch (err) {
          console.warn('[voice] parseServerWsMessage failed', {
            err: err instanceof Error ? err.message : String(err),
            rawPreview: data.slice(0, 240),
          });
          return;
        }
        if (parsed.type === 'pronunciation_report' || parsed.type === 'pronunciation_correction') {
          console.info('[voice] PA msg', parsed.type, parsed);
        }
        serverHandler.handleJson(parsed);
      });

      ws.addEventListener('close', (event: CloseEvent) => {
        // Ignorar el close de un socket viejo (carrera tras un swap).
        if (wsRef.current !== ws) return;
        wsRef.current = null;
        // Un turno en post-roll no llegó a mandar su `turn-stop`: se perdió
        // (aviso "no se escuchó"), salvo que el cierre sea deliberado.
        actionsRef.current.abandonPendingCommit({ lost: !intentionalCloseRef.current });
        // Un commit en vuelo murió con el socket — que la UI no lo espere.
        clearCommitPending();
        // Y lo dicho a medias tampoco llegó a ningún lado: sin esto la burbuja
        // en vivo quedaba para siempre (Emmanuel, 14/10: «no termina de
        // escuchar nunca» tras una caída del server con un turno a medio decir).
        setTranscript('');
        // El socket murió antes de probarse estable → no reseteamos el contador.
        clearStableTimer();
        // Corte por saldo: normalmente el `session.blocked` previo ya marcó el
        // cierre, pero los dos emisores lo mandan dentro de un `try/catch` con
        // el WS cerrándose — si ese `send` falla, el código de cierre es lo
        // único que llega.
        if (event.code === WS_CLOSE_INSUFFICIENT_CREDITS) markServerSessionClosed();
        if (intentionalCloseRef.current) {
          // Cierre deliberado (`stop()`/unmount, o cierre server-side vía
          // `session.ended`/`session.blocked`): no reconectar. El cleanup
          // suelta mic/player si el cierre lo inició el SERVER (en el path
          // app-stop ya corrió y es idempotente — refs en null).
          setConnectionStatus('idle');
          setState('idle');
          void cleanup();
          return;
        }
        // Caída inesperada → el turno en curso se interrumpe; reconectar.
        setState('idle');
        scheduleReconnectRef.current();
      });

      ws.addEventListener('error', () => {
        // El 'error' del WS siempre va seguido de 'close', que maneja la
        // reconexión. No marcamos `state:'error'` para no flashear el orb.
        console.warn('[voice] WS error (reconnect driven by close)');
      });
    },
    [
      opts,
      setState,
      armMicSettle,
      setSessionIdTracked,
      clearStableTimer,
      cleanup,
      markServerSessionClosed,
      setIsAudioPlayingFocused,
    ],
  );

  // Programa el próximo reintento con backoff exponencial. Al agotar los
  // intentos pasa a `'lost'` (terminal hasta `reconnect()` manual).
  const scheduleReconnect = useCallback((): void => {
    const attempt = reconnectAttemptsRef.current;
    if (attempt >= MAX_RECONNECT_ATTEMPTS) {
      console.warn('[voice] reconnect attempts exhausted → lost');
      setConnectionStatus('lost');
      void cleanup();
      return;
    }
    setConnectionStatus('reconnecting');
    reconnectAttemptsRef.current = attempt + 1;
    const delay = reconnectDelayMs(attempt);
    console.info('[voice] scheduling reconnect', { attempt: attempt + 1, delayMs: delay });
    reconnectTimerRef.current = setTimeout(() => {
      reconnectTimerRef.current = null;
      attemptReconnectRef.current();
    }, delay);
  }, [cleanup]);

  // Ejecuta un intento de reconexión: recrea devices si hace falta y reabre el
  // WS con `reconnectSessionId` = sesión caída (reattach a la MISMA fila). Si
  // falla, reprograma (o agota).
  const attemptReconnect = useCallback((): void => {
    if (intentionalCloseRef.current || wsRef.current) return;
    void ensureDevices()
      .then(() => {
        if (intentionalCloseRef.current || wsRef.current) return;
        openConnection(sessionIdRef.current ?? undefined);
      })
      .catch(() => {
        scheduleReconnectRef.current();
      });
  }, [ensureDevices, openConnection]);

  // Sincroniza los refs que rompen el ciclo de callbacks recursivos.
  scheduleReconnectRef.current = scheduleReconnect;
  attemptReconnectRef.current = attemptReconnect;

  const start = useCallback(async (startOpts?: { inputMode?: ClientIOMode['input'] }) => {
    if (wsRef.current) return;
    // Canal de entrada fijado ANTES del handshake (degradación a chat sin mic):
    // `onWsOpen` lee `ioModeRef` para el `startMode`, así que en `'text'` el
    // server arranca con el STT en bypass y acá ni se intenta el `mic.start()`.
    // No pasa por `setIoMode()` a propósito: ese manda un `mode.switch` que con
    // el WS todavía cerrado se perdería.
    const inputMode = startOpts?.inputMode;
    if (inputMode !== undefined && inputMode !== ioModeRef.current.input) {
      const nextIo: ClientIOMode = { input: inputMode, output: ioModeRef.current.output };
      ioModeRef.current = nextIo;
      setIoModeState(nextIo);
    }
    intentionalCloseRef.current = false;
    reconnectAttemptsRef.current = 0;
    lostAutoRetriesRef.current = 0;
    clearReconnectTimer();
    clearStableTimer();
    // F5 survival: si hay un sessionId persistido FRESCO (charla que se cortó por
    // recarga/crash de la página hace < TTL), lo mandamos como `reconnectSessionId`
    // para reattachear la MISMA fila. Se lee ANTES del reset (el reset no borra el
    // storage). Si no hay o venció → arranque limpio (comportamiento previo).
    const reattachId = readFreshReattachSession(opts.moduleSlug);
    // Reset sessionId — `session.started` lo emite tras handshake.
    setSessionIdTracked(null);
    setConnectionStatus('connecting');
    try {
      await ensureDevices();
    } catch (err) {
      setConnectionStatus('idle');
      throw err;
    }
    openConnection(reattachId);
  }, [
    clearReconnectTimer,
    clearStableTimer,
    ensureDevices,
    openConnection,
    setSessionIdTracked,
    opts.moduleSlug,
  ]);

  // Reabre desde `lost` con reattach a la misma fila (o fallback a resume si ya
  // no es elegible). Repone la ráfaga con backoff pero NO el presupuesto de
  // auto-reintentos: lo comparten el botón manual y el efecto de auto-reconexión
  // de abajo, y si esto lo reseteara el tope de ese efecto nunca se alcanzaría.
  const reopenFromLost = useCallback((): void => {
    if (wsRef.current || reconnectTimerRef.current) return;
    intentionalCloseRef.current = false;
    reconnectAttemptsRef.current = 0;
    setConnectionStatus('reconnecting');
    void ensureDevices()
      .then(() => {
        if (intentionalCloseRef.current || wsRef.current) return;
        openConnection(sessionIdRef.current ?? undefined);
      })
      .catch(() => setConnectionStatus('lost'));
  }, [ensureDevices, openConnection]);

  // Reconexión MANUAL (botón en estado `'lost'`). El tap del user es una señal
  // fuerte ("probá de nuevo"): repone también el presupuesto de auto-reintentos.
  const reconnect = useCallback((): void => {
    lostAutoRetriesRef.current = 0;
    reopenFromLost();
  }, [reopenFromLost]);

  // Auto-reconexión DESDE `lost`: la ráfaga con backoff se agota en ~15s, así
  // que una caída más larga (backend reiniciando, túnel/wifi que vuelve al
  // minuto) dejaba la charla muerta esperando un tap. Acá seguimos intentando
  // solos, con tres disparadores y un presupuesto acotado:
  //
  //  - `online` → la red volvió según el browser: reponemos el presupuesto e
  //    intentamos ya.
  //  - tab visible de nuevo → el user está mirando la pantalla; ídem.
  //  - cada `LOST_AUTO_RETRY_MS`, hasta `MAX_LOST_AUTO_RETRIES`, haya o no red
  //    según el browser, pero sólo con el tab VISIBLE (en background el mic está
  //    soltado y el AudioContext suspendido: reabrir ahí gasta una sesión que
  //    arranca muda).
  //
  // El tope existe por lo mismo que `STABLE_CONNECTION_MS`: cada reintento que
  // conecta y se cae arma una sesión nueva con su saludo. Ojo si tocás esto —
  // sin tope y sin gate de visibilidad se vuelve el loop infinito que ese
  // mecanismo justamente evita.
  useEffect(() => {
    if (connectionStatus !== 'lost') return;

    const retry = (source: 'interval' | 'online' | 'visible'): void => {
      if (document.visibilityState !== 'visible') return;
      // NO gateamos por `navigator.onLine`: con el celu sin red el intervalo
      // igual reintenta. Cuesta nada (el WS falla al instante sin red) y compra
      // dos cosas — el user VE que la app está intentando en vez de una card
      // muerta, y no dependemos de que llegue el evento `online` (en WebViews /
      // PWA no siempre llega, y ahí la charla quedaba trabada hasta el tap).
      if (lostAutoRetriesRef.current >= MAX_LOST_AUTO_RETRIES) return;
      lostAutoRetriesRef.current += 1;
      console.info('[voice] auto-reconnect desde lost', {
        source,
        attempt: lostAutoRetriesRef.current,
      });
      reopenFromLost();
    };

    const timer = setInterval(() => retry('interval'), LOST_AUTO_RETRY_MS);
    const onOnline = (): void => {
      lostAutoRetriesRef.current = 0;
      retry('online');
    };
    const onVisible = (): void => {
      if (document.visibilityState !== 'visible') return;
      lostAutoRetriesRef.current = 0;
      retry('visible');
    };
    window.addEventListener('online', onOnline);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(timer);
      window.removeEventListener('online', onOnline);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [connectionStatus, reopenFromLost]);

  const stop = useCallback(async () => {
    // Marca el cierre como deliberado ANTES de cerrar el WS → el close handler
    // no dispara reconexión.
    intentionalCloseRef.current = true;
    reconnectAttemptsRef.current = 0;
    // Un turno en post-roll no se despacha contra una sesión que se cierra.
    actionsRef.current.abandonPendingCommit({ lost: false });
    // Cierre deliberado: la charla terminó a propósito → no reattachear tras un
    // F5. (El backend también la cierra como `client-end`, no reattach-elegible.)
    clearReattachSession(opts.moduleSlug);
    await cleanup();
    setConnectionStatus('idle');
    // Bot pendiente al cortar: persistimos el texto suelto como mensaje
    // cerrado para que el historial refleje lo que se vio en pantalla.
    if (assistantTextRef.current) {
      const text = assistantTextRef.current;
      const pendingTr = pendingBotTranslationRef.current;
      pendingBotTranslationRef.current = null;
      const pendingSrc = pendingBotWebSourcesRef.current;
      pendingBotWebSourcesRef.current = null;
      setLiveWebSources([]);
      setMessages((m) => [
        ...m,
        {
          who: 'bot',
          text,
          ...(pendingTr ? { translation: pendingTr } : {}),
          ...(pendingSrc && pendingSrc.length > 0 ? { sources: pendingSrc } : {}),
        },
      ]);
      assistantTextRef.current = '';
      karaokeRef.current.tokensStartedAtMs = null;
    }
    realtimeLiveResponseIdRef.current = null;
    backgroundAutoPausedRef.current = false;
    setState('idle');
    prevStateRef.current = 'idle';
    setTranscript('');
    setAssistantText('');
    clearCommitPending();
    setReplyFailed(false);
    setSessionIdTracked(null);
    setMuted(false);
    mutedRef.current = false;
    setPaused(false);
    pausedRef.current = false;
    setIsAudioPlayingFocused(false);
  }, [
    cleanup,
    setState,
    setSessionIdTracked,
    clearCommitPending,
    setIsAudioPlayingFocused,
    opts.moduleSlug,
  ]);

  // Reanudar con audio del coach todavía en la cola = ese audio vuelve a sonar,
  // así que vuelve a ser el dueño del foco. Sin esto, lo que el usuario haya
  // puesto a sonar durante la pausa (el replay de una burbuja) seguiría sonando
  // encima del coach al despausar.
  useEffect(() => {
    if (paused) return;
    if (!playerRef.current?.isPlaying?.()) return;
    setIsAudioPlayingFocused(true);
  }, [paused, setIsAudioPlayingFocused]);

  const clearMessages = useCallback(() => setMessages([]), []);

  // Siembra el historial visible al retomar una charla (flujo "Continuar").
  // Sólo siembra si está vacío: así, en la carrera improbable de que el primer
  // turno vivo llegue antes que el fetch del transcript, no pisamos contenido
  // real. El gating del `start()` (historyReady en el controller) hace que en
  // la práctica esto corra siempre antes de abrir el WS.
  const seedHistory = useCallback((seed: ReadonlyArray<VoiceMessage>) => {
    setMessages((prev) => (prev.length === 0 ? seed.slice() : prev));
  }, []);

  // Acciones públicas — todas tocan el WS o refs. La factoría las arma
  // bound a refs estables; usamos useRef de la última `translationTargetLang`
  // para que setTranslationEnabled vea el valor actual sin deps.
  const translationTargetLangRef = useRef(translationTargetLang);
  translationTargetLangRef.current = translationTargetLang;

  const actionsRef = useRef(
    buildSessionActions({
      wsRef,
      micRef,
      playerRef,
      pendingAcks,
      interruptSeq,
      pausedRef,
      mutedRef,
      captureExternallyPausedRef,
      ioModeRef,
      assistantTextRef,
      pendingBotTranslationRef,
      pendingBotWebSourcesRef,
      setLiveWebSources,
      onInterruptForceListening: () => {
        if (stateRef.current === 'speaking' || stateRef.current === 'thinking') {
          setState('listening');
          prevStateRef.current = 'listening';
        }
      },
      onClearKaraokeOnInterrupt: () => clearKaraokeBuffer(karaokeRef.current),
      getReplyPlayback: () => {
        const pb = playerRef.current?.getTurnPlayback();
        if (!pb || pb.receivedMs <= 0) return null;
        return { ...pb, complete: turnAudioCompleteRef.current };
      },
      setTranscript,
      setAssistantText,
      setPaused,
      setMuted,
      setIsAudioPlaying: setIsAudioPlayingFocused,
      setMessages,
      setIoModeState,
      setTranslationEnabledState,
      setTranslationTargetLangState,
      getTranslationTargetLang: () => translationTargetLangRef.current,
      setSpeechRateState,
      onCommitPending: markCommitPending,
      pendingCommitRef,
      onCommitLost: onTurnEmpty,
      clearCommitPending,
    }),
  );

  // Un turno nuevo del user deja atrás el reply que había fallado: si el nuevo
  // también falla, el server manda otro `reply_failed`.
  const commitTurnFn = useCallback((commitOpts?: CommitTurnOptions): void => {
    setReplyFailed(false);
    actionsRef.current.commitTurn(commitOpts);
  }, []);
  const sendTextFn = useCallback((text: string, sendOpts?: { fromSuggestion?: boolean }): void => {
    setReplyFailed(false);
    actionsRef.current.sendText(text, sendOpts);
  }, []);
  // Reintentar el reply fallido: el server re-despacha el MISMO contexto (el
  // user no repite nada). Sin WS abierto no hay a quién pedírselo — la app ya
  // muestra el estado de conexión por su lado.
  const retryReply = useCallback((): void => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    try {
      ws.send(encodeClientWsMessage({ type: 'reply.retry' }));
    } catch {
      return;
    }
    setReplyFailed(false);
  }, []);

  // Game controls (plan 47): a `session.update { gameAction }` the server turns
  // into a coach turn. Same channel and same no-WS behaviour as any update.
  const sendGameAction = useCallback((action: GameActionRequest): void => {
    actionsRef.current.sendSessionUpdate(gameActionPatch(action));
  }, []);

  // Getters del karaoke — wrappers estables sobre las funciones puras.
  const getKaraokeCharIndexFn = useCallback(
    (): number => getKaraokeCharIndex(karaokeRef.current, playerRef.current),
    [],
  );
  const getKaraokeRenderEndIdxFn = useCallback(
    (): number => getKaraokeRenderEndIdx(karaokeRef.current, playerRef.current),
    [],
  );

  // Getter de "energía" del orb — lee del state via ref, sin re-renderear.
  // El consumidor lo invoca desde un RAF y lo escribe como CSS var.
  const getOrbEnergyFn = useCallback((): number => {
    // Modo realtime (F4a): full-duplex — ambos canales pueden sonar a la vez,
    // así que el orb no puede gatear por `state` (quedaba "ciego" al user
    // hablando encima del bot). Energía = max(mic, bot); mute/pause siguen
    // apagando el canal del mic (son intención del user). Para UIs de DOS
    // canales están `getMicEnergy` + `getBotEnergy` por separado.
    if (realtimeModeRef.current) {
      const micRms =
        mutedRef.current || pausedRef.current ? 0 : (micRef.current?.getRms?.() ?? 0);
      const botRms = playerRef.current?.getOutputRms?.() ?? 0;
      return Math.max(micRms, botRms);
    }
    const s = stateRef.current;
    if (s === 'listening') {
      // Si el mic está muted o la sesión pausada, no hay energía real.
      if (mutedRef.current || pausedRef.current) return 0;
      const mic = micRef.current;
      return mic?.getRms?.() ?? 0;
    }
    if (s === 'speaking') {
      const player = playerRef.current;
      return player?.getOutputRms?.() ?? 0;
    }
    return 0;
  }, []);

  // Getter de energía del MIC sin gatear por `state`. `getOrbEnergy` solo
  // devuelve RMS del mic cuando el server está en `listening`; para UIs que
  // muestran una waveform mientras el mic está abierto (p.ej. la barra de
  // grabación de apps/speak en modo toggle/hold, donde el `recording` local
  // no siempre coincide con `state==='listening'`), este getter devuelve el
  // RMS del mic siempre que esté capturando y no esté muteado/pausado.
  const getMicEnergyFn = useCallback((): number => {
    if (mutedRef.current || pausedRef.current) return 0;
    return micRef.current?.getRms?.() ?? 0;
  }, []);

  // Canal BOT sin gatear por `state` (espejo de `getMicEnergy`): RMS del
  // audio del bot sonando AHORA. En realtime los dos canales se publican en
  // paralelo (F4a) — una UI de dos canales lee mic + bot por separado.
  const getBotEnergyFn = useCallback((): number => {
    return playerRef.current?.getOutputRms?.() ?? 0;
  }, []);

  // Publicamos el getter en el canal singleton para que el `<Orb/>` pueda
  // leerlo sin prop drilling. Limpia al unmount.
  useEffect(() => {
    setOrbEnergyGetter(getOrbEnergyFn);
    return () => setOrbEnergyGetter(null);
  }, [getOrbEnergyFn]);

  // Getter: ¿ya empezó a llegar texto del bot en este turno? Lo usa el
  // Orb para apagar el ring rotante de `thinking` apenas el primer token
  // del LLM se ve en pantalla, sin tener que esperar al `TTSStartedFrame`
  // (que puede llegar 100-300ms después). Mientras tanto la sphere queda
  // quieta; cuando arranca el audio, el state pasa a `speaking` y las
  // ondas hacia afuera toman el lugar.
  const getOrbResponseStartedFn = useCallback((): boolean => {
    return assistantTextRef.current.trim().length > 0;
  }, []);

  useEffect(() => {
    setOrbResponseStartedGetter(getOrbResponseStartedFn);
    return () => setOrbResponseStartedGetter(null);
  }, [getOrbResponseStartedFn]);

  // Getter: ¿hay audio TTS sonando ahora mismo? Se usa para que la orb
  // siga mostrando `speaking` (ondas afuera) durante el gap entre
  // `state: listening` (server terminó de mandar chunks) y el fin
  // acústico real (los chunks siguen sonando varios cientos de ms).
  const getOrbAudioPlayingFn = useCallback((): boolean => {
    const player = playerRef.current;
    return player?.isPlaying?.() ?? false;
  }, []);

  useEffect(() => {
    setOrbAudioPlayingGetter(getOrbAudioPlayingFn);
    return () => setOrbAudioPlayingGetter(null);
  }, [getOrbAudioPlayingFn]);

  // Cleanup on unmount — cierre deliberado (no reconectar). Con
  // `reattachOnUnmount` la desconexión es SILENCIOSA (sin `{type:'end'}`): el
  // backend deja la fila reattach-elegible (client-disconnect) y NO limpiamos el
  // id persistido, así una remontada dentro de la ventana de gracia reattachea
  // la MISMA sesión (mismo pipeline, un solo bill). Sin la opción, es un fin
  // deliberado como siempre (client-end). Ref para leer el valor vivo sin
  // re-suscribir el efecto de unmount.
  const reattachOnUnmountRef = useRef(opts.reattachOnUnmount ?? false);
  reattachOnUnmountRef.current = opts.reattachOnUnmount ?? false;
  useEffect(() => {
    return () => {
      intentionalCloseRef.current = true;
      void cleanup(reattachOnUnmountRef.current ? { silent: true } : {});
    };
  }, [cleanup]);

  return {
    state,
    connectionStatus,
    reconnect,
    transcript,
    assistantText,
    messages,
    liveWebSources,
    sessionId,
    muted,
    paused,
    start,
    stop,
    clearMessages,
    seedHistory,
    interrupt: actionsRef.current.interrupt,
    commitTurn: commitTurnFn,
    discardTurn: actionsRef.current.discardTurn,
    cancelTurn: actionsRef.current.cancelTurn,
    toggleMute: actionsRef.current.toggleMute,
    pauseSession: actionsRef.current.pauseSession,
    resumeSession: actionsRef.current.resumeSession,
    pauseCapture: actionsRef.current.pauseCapture,
    resumeCapture: actionsRef.current.resumeCapture,
    sendSessionUpdate: actionsRef.current.sendSessionUpdate,
    gameRound,
    gameSolutions,
    sendGameAction,
    sendVoiceChange: actionsRef.current.sendVoiceChange,
    setOutputVolume: actionsRef.current.setOutputVolume,
    lastTtsFirstByteMs,
    isAudioPlaying,
    audioRecording,
    conversationMode,
    commitPending,
    turnEmptyCount,
    replyFailed,
    retryReply,
    getKaraokeCharIndex: getKaraokeCharIndexFn,
    getKaraokeRenderEndIdx: getKaraokeRenderEndIdxFn,
    getOrbEnergy: getOrbEnergyFn,
    getMicEnergy: getMicEnergyFn,
    getBotEnergy: getBotEnergyFn,
    ioMode,
    setIoMode: actionsRef.current.setIoMode,
    sendText: sendTextFn,
    translationEnabled,
    translationTargetLang,
    setTranslationEnabled: actionsRef.current.setTranslationEnabled,
    speechRate,
    setSpeechRate: actionsRef.current.setSpeechRate,
  };
}
